import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createConvo, setConvo } from "../Redux/Actions/convoActions";
import { getValidChatSession, clearChatSession, getValidDexySession, clearDexySession } from "../utils/sessionManager";
import { kbService } from "../utils/kbService";
import { X, Search, ChevronRight, ArrowUp, Paperclip, Mic, Sparkles, MessageSquare, Pin } from "lucide-react";
import logo from "../assets/logo.png";

const Card = ({
  type,
  imageUrl,
  title,
  description,
  tags = [],
  primaryColor = "#0a2f96",
}) => {
  return (
    <div className="relative block mx-4 mb-3 bg-white rounded-2xl border border-slate-200/70 shadow-sm hover:shadow-md transition-all overflow-hidden cursor-pointer">
      {type && (
        <div className="absolute top-2.5 right-2.5 z-10">
          <span
            className="text-[10px] font-semibold px-2 py-0.5 rounded-full text-white"
            style={{ backgroundColor: primaryColor }}
          >
            {type}
          </span>
        </div>
      )}

      {imageUrl && (
        <img
          className="w-full h-32 object-cover"
          src={imageUrl}
          alt={title}
        />
      )}

      <div className="p-4">
        {tags?.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mb-2">
            {tags.map((tag, idx) => (
              <span
                key={idx}
                className="text-[10px] font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        <div className="font-semibold text-slate-900 text-sm">{title}</div>
        {description && (
          <div className="text-xs text-slate-500 mt-1 line-clamp-2">
            {description}
          </div>
        )}
      </div>
    </div>
  );
};

function LandingWindow({
  setView,
  setupDetails,
  setIsOpen,
  setActiveIcon,
  widgetConfigs,
  setInitialDexyMessage,
  setSelectedArticle,
  conversations = [],
  setSelectedConversation,
}) {
  const dispatch = useDispatch();
  const convo = useSelector((state) => state.convo);
  const [quickInput, setQuickInput] = useState("");
  const [frequentlyReadArticles, setFrequentlyReadArticles] = useState([]);
  const [featuredArticle, setFeaturedArticle] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);

  // Filter ticket conversations (exclude subSource === 'convo')
  const isTicketEnabled = setupDetails?.widgetCustomizations?.ticket !== false;

  const ticketConversations = (conversations || [])
    .filter((conv) => {
      if (!conv || !conv.ticketId) return false;
      const subSource = (
        conv.ticketId?.subSource ||
        conv?.subSource ||
        ""
      )
        .toLowerCase()
        .trim();
      return subSource !== "convo";
    })
    .sort((a, b) => {
      const timeA = new Date(
        a?.ticketId?.lastMessageAt || a?.updatedAt || a?.createdAt || 0
      ).getTime();
      const timeB = new Date(
        b?.ticketId?.lastMessageAt || b?.updatedAt || b?.createdAt || 0
      ).getTime();
      return timeB - timeA;
    });

  const recentTickets = ticketConversations.slice(0, 3);

  const formatTicketTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24 && date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const renderTicketStatusBadge = (status) => {
    const s = String(status || "open").toLowerCase().trim();
    let dotColor = "bg-emerald-500";
    let bgClasses = "bg-emerald-50 text-emerald-700 border-emerald-200/80";
    let label = s.replace(/_/g, " ");

    if (["closed", "cancelled", "duplicate", "merged", "resolved"].includes(s)) {
      dotColor = "bg-slate-400";
      bgClasses = "bg-slate-50 text-slate-600 border-slate-200/80";
    } else if (["in_progress", "pending", "on_hold", "assigned"].includes(s)) {
      dotColor = "bg-amber-500";
      bgClasses = "bg-amber-50 text-amber-700 border-amber-200/80";
    }

    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[10px] font-semibold border ${bgClasses} select-none`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
        <span className="capitalize">{label}</span>
      </span>
    );
  };

  const partnerId =
    widgetConfigs?.uniquePartnerId ||
    setupDetails?.uniquePartnerId ||
    localStorage.getItem("uniquePartnerId");

  useEffect(() => {
    if (!partnerId) return;
    let isMounted = true;

    // Only call knowledgebase API if help module is enabled
    const isHelpEnabled = Boolean(setupDetails?.widgetCustomizations?.help);

    if (!isHelpEnabled) {
      setAnnouncements([]);
      setFrequentlyReadArticles([]);
      setFeaturedArticle(null);
      return;
    }

    // Fetch latest announcement / news articles up to maxAnnouncements (max 5)
    const maxAnnouncements =
      setupDetails?.widgetCustomizations?.maxAnnouncements !== undefined
        ? Number(setupDetails.widgetCustomizations.maxAnnouncements)
        : 1;

    if (maxAnnouncements > 0) {
      kbService
        .getAnnouncements(partnerId)
        .then((allAnnouncements) => {
          if (isMounted && allAnnouncements && allAnnouncements.length > 0) {
            const newsArticles = allAnnouncements.filter(
              (art) => art.articleType === "news" || art.articleType === "announcement"
            );
            newsArticles.sort((a, b) => {
              if (Boolean(b.isFeatured) !== Boolean(a.isFeatured)) {
                return b.isFeatured ? 1 : -1;
              }
              const dateB = new Date(b.updatedAt || b.DateofUpdation || b.createdAt || 0).getTime();
              const dateA = new Date(a.updatedAt || a.DateofUpdation || a.createdAt || 0).getTime();
              return dateB - dateA;
            });
            setAnnouncements(newsArticles.slice(0, maxAnnouncements));
          } else {
            setAnnouncements([]);
          }
        })
        .catch((err) =>
          console.error("Error loading news in LandingWindow:", err)
        );
    } else {
      setAnnouncements([]);
    }

    setLoadingArticles(true);

    Promise.all([
      kbService.getMostViewedArticles(partnerId, 4),
      kbService.getFeaturedArticles(partnerId),
    ])
      .then(([mostViewed, featured]) => {
        if (!isMounted) return;
        if (featured && featured.length > 0) {
          setFeaturedArticle(featured[0]);
        }
        if (mostViewed && mostViewed.length > 0) {
          setFrequentlyReadArticles(mostViewed.slice(0, 4));
        } else if (featured && featured.length > 0) {
          setFrequentlyReadArticles(featured.slice(0, 4));
        } else {
          kbService.getPublishedArticles(partnerId).then((published) => {
            if (isMounted && published && published.length > 0) {
              setFrequentlyReadArticles(published.slice(0, 4));
            }
          });
        }
      })
      .catch((err) => console.error("Error loading articles in LandingWindow:", err))
      .finally(() => {
        if (isMounted) setLoadingArticles(false);
      });

    return () => {
      isMounted = false;
    };
  }, [partnerId, setupDetails?.widgetCustomizations?.help, setupDetails?.widgetCustomizations?.maxAnnouncements]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const toggleListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }

    if (isListening) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      const existingText = quickInput ? quickInput.trim() + " " : "";

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event) => {
        let interimTranscript = "";
        let finalTranscript = "";
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interimTranscript += event.results[i][0].transcript;
          }
        }
        setQuickInput(existingText + finalTranscript + interimTranscript);
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.error("Error starting speech recognition:", err);
      setIsListening(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      alert("File size exceeds 20MB limit.");
      return;
    }
    setAttachedFile(file);
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("attachment", file);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_CHAT_MICROSERVICE_URL}/attachment`,
        {
          method: "POST",
          body: formData,
        }
      );
      if (!response.ok) throw new Error("Upload failed");
      const result = await response.json();
      return result?.data?.url || result?.url || null;
    } catch (err) {
      console.error("Error uploading file in LandingWindow:", err);
      return null;
    }
  };

  const handleQuickAsk = async (textOverride) => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
    const text = (typeof textOverride === "string" ? textOverride : quickInput).trim();
    if (!text && !attachedFile) return;

    let finalMessage = text;

    if (attachedFile) {
      setIsUploading(true);
      const fileUrl = await uploadFile(attachedFile);
      setIsUploading(false);
      if (fileUrl) {
        finalMessage = text ? `${text}\n${fileUrl}` : fileUrl;
      } else {
        finalMessage = text
          ? `${text} (Attachment: ${attachedFile.name})`
          : `Attached: ${attachedFile.name}`;
      }
    }

    setAttachedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setQuickInput("");

    // If the existing session has a resolved / closed ticket, clear it so we start a fresh chat
    const partnerId = widgetConfigs?.uniquePartnerId;
    if (partnerId) {
      const existingSession = getValidChatSession(partnerId);
      const existingDexySession = getValidDexySession(partnerId);
      const existingStatus =
        existingSession?.conversation?.ticketId?.status ||
        existingDexySession?.conversation?.ticketId?.status ||
        "";
      const isEndedConvo = ["resolved", "Resolved", "closed", "Closed"].includes(existingStatus);
      if (isEndedConvo) {
        clearChatSession(partnerId);
        clearDexySession(partnerId);
        dispatch(setConvo({}));
      }
    }

    if (setInitialDexyMessage && finalMessage) {
      setInitialDexyMessage(finalMessage);
    }
    const isLegacy = setupDetails?.widgetWorkflow?.workflowId === 'legacy';
    setActiveIcon("ai");
    setView(isLegacy ? "AutomatedBot" : "DexyAI");
  };

  const color1 = setupDetails?.widgetCustomizations?.color1 || setupDetails?.widgetCustomizations?.primaryColor || "#0C2A4D";
  const color2 = setupDetails?.widgetCustomizations?.color2 || setupDetails?.widgetCustomizations?.secondaryColor || "#2060EA";
  const color3 = setupDetails?.widgetCustomizations?.color3 || "#FFFFFF";
  const headerBgType = setupDetails?.widgetCustomizations?.headerBgType || "color";
  const headerBgImage = setupDetails?.widgetCustomizations?.headerBgImage || "";
  const headerGradientAnimate = setupDetails?.widgetCustomizations?.headerGradientAnimate || false;
  const uiColor = setupDetails?.widgetCustomizations?.uiColor || color1 || "#1C243A";
  const primaryColor = color1;
  const backgroundColor = setupDetails?.widgetCustomizations?.backgroundColor || "#FFFFFF";
  const composerGlow = setupDetails?.widgetCustomizations?.composerGlow !== false;
  const auroraColors = setupDetails?.widgetCustomizations?.auroraColors && setupDetails?.widgetCustomizations.auroraColors.length > 0
    ? setupDetails.widgetCustomizations.auroraColors
    : [color2, "#60a5fa", "#818cf8", "#a78bfa", color1];

  return (
    <div 
      className="w-full text-slate-800 min-h-full transition-colors bg-white"
      style={{ backgroundColor: "#ffffff" }}
    >
      {/* --- Gleap-Style Header with Partner Selected Colors --- */}
      <div 
        className="pt-6 px-6 pb-10 text-white relative transition-all duration-300"
        style={{
          background:
            headerBgType === "image" && headerBgImage
              ? `url(${headerBgImage}) center/cover no-repeat`
              : `linear-gradient(180deg, ${color1 || '#000000'} 0%, ${color2 || '#111827'} 60%, #ffffff 100%)`,
          backgroundSize: headerGradientAnimate ? "180% 180%" : "100% 100%",
          animation: headerGradientAnimate ? "pulse 6s ease-in-out infinite alternate" : "none",
        }}
      >
        {/* Top bar: Brand logo on left & partner name, Close button on right */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-1 mr-2 overflow-hidden">
            {(setupDetails?.widgetCustomizations?.logoUrl || logo) && (
              <img
                className="h-7 max-h-7 max-w-[150px] w-auto object-contain shrink-0"
                alt="Logo"
                src={setupDetails?.widgetCustomizations?.logoUrl || logo}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            )}
            {setupDetails?.widgetCustomizations?.partnerName &&
              setupDetails.widgetCustomizations.partnerName.trim() !== "" && (
                <span className="font-semibold text-xl sm:text-2xl tracking-tight text-base text-white truncate">
                  {setupDetails.widgetCustomizations.partnerName}
                </span>
              )}
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all cursor-pointer border-none outline-none"
            title="Close"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
         <div className="h-[50px]"></div>
        {/* Greeting & Main Header - Spacious Gleap-style layout */}
        <div className="mt-9 sm:mt-9 mb-7">
          <div className="text-white/95 text-xl sm:text-2xl font-semibold tracking-tight">
            Hi{widgetConfigs?.userName ? ` ${widgetConfigs.userName.split(" ")[0]}` : ""} 👋
          </div>
          <h2 className="text-white text-xl sm:text-2xl font-semibold tracking-tight mt-1 leading-snug">
            {setupDetails?.widgetCustomizations?.homePageHeading || "How can we help you today?"}
          </h2>
        </div>

        {/* Ask me anything Composer Box */}
        <div className="relative">
          {composerGlow && (
            <div
              className="absolute -inset-1 rounded-2xl blur-md opacity-60 pointer-events-none transition-all duration-500"
              style={{
                background: `linear-gradient(135deg, ${color2 || '#3b82f6'}, ${auroraColors[1] || '#818cf8'}, ${color1 || '#000000'})`
              }}
            />
          )}
          <div className="relative bg-white rounded-2xl p-4 shadow-[0_12px_36px_rgba(0,0,0,0.08)] border border-slate-100/80 text-slate-800 transition-all">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
            />
            <input
              type="text"
              placeholder={isListening ? "Listening... Speak now 🎙️" : "Ask me anything..."}
              value={quickInput}
              onChange={(e) => setQuickInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isUploading) handleQuickAsk();
              }}
              className="w-full text-sm text-slate-800 placeholder-slate-400 bg-transparent border-0 outline-none focus:outline-none focus:ring-0 focus:border-transparent focus:shadow-none p-0"
              style={{ outline: "none", border: "none", boxShadow: "none" }}
            />

            {/* Attached file preview chip */}
            {attachedFile && (
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200/90 rounded-xl px-2.5 py-1.5 mt-2.5 text-xs text-slate-700 animate-in fade-in">
                <div className="flex items-center gap-1.5 truncate max-w-[240px]">
                  <Paperclip className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                  <span className="truncate font-medium text-slate-800">{attachedFile.name}</span>
                  <span className="text-[10px] text-slate-400 shrink-0">
                    ({(attachedFile.size / 1024).toFixed(0)} KB)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setAttachedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                  className="w-4 h-4 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors border-none bg-transparent cursor-pointer ml-1"
                  title="Remove file"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}

            <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-slate-100">
              <div className="flex items-center gap-2 text-slate-400">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center disabled:opacity-50"
                  title="Attach file"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={toggleListening}
                  className={`p-1 rounded-full transition-all border-none outline-none cursor-pointer flex items-center justify-center ${
                    isListening
                      ? "bg-red-100 text-red-600 animate-pulse ring-2 ring-red-400"
                      : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                  }`}
                  title={isListening ? "Listening... Click to stop" : "Speech to text"}
                >
                  <Mic className="w-4 h-4" />
                </button>
                {isListening && (
                  <span className="text-[11px] font-medium text-red-500 animate-pulse select-none">
                    Listening...
                  </span>
                )}
              </div>
              <button
                onClick={() => handleQuickAsk()}
                disabled={isUploading}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-all cursor-pointer border-none outline-none text-white hover:scale-105 shadow-sm disabled:opacity-60"
                style={{ backgroundColor: uiColor || '#000000' }}
                title={isUploading ? "Uploading..." : "Ask Dexy AI"}
              >
                {isUploading ? (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <ArrowUp className="w-4 h-4 stroke-[2.5]" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* --- Quick Action Pills & Recent Tickets (Shown if Ticket is enabled) --- */}
      {isTicketEnabled && (
        <div className="space-y-3 mt-0">
          <div className="flex items-center gap-2.5 px-4">
            <button
              onClick={() => {
                setActiveIcon("tickets");
                setView("chatList");
              }}
              className="flex-1 bg-white hover:bg-slate-50 border border-slate-200/90 rounded-full py-2 px-3.5 text-xs font-semibold text-slate-800 shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-center justify-center gap-2 cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <span className="text-base leading-none">🐞</span>
              <span>Report an issue</span>
            </button>
            <button
              onClick={() => {
                setActiveIcon("roadmap");
                setView("Roadmap");
              }}
              className="flex-1 bg-white hover:bg-slate-50 border border-slate-200/90 rounded-full py-2 px-3.5 text-xs font-semibold text-slate-800 shadow-[0_2px_8px_rgba(0,0,0,0.04)] flex items-center justify-center gap-2 cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <span className="text-base leading-none">💡</span>
              <span>Request a feature</span>
            </button>
          </div>

          {/* --- Professional Recent Tickets List Card (Up to 3 tickets) --- */}
          {recentTickets.length > 0 && (
            <div className="mx-4 bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
              <div className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50/70 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-800 tracking-tight">Recent Tickets</span>
                  <span className="bg-indigo-50 text-indigo-600 text-[10px] font-semibold px-2 py-0.5 rounded-full border border-indigo-100">
                    {ticketConversations.length}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setActiveIcon("tickets");
                    setView("chatList");
                  }}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-0.5 cursor-pointer border-none bg-transparent"
                >
                  <span>View all</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="divide-y divide-slate-100">
                {recentTickets.map((conv, idx) => {
                  const ticket = conv.ticketId || {};
                  const ticketNum = ticket.ticketNumber ? `#${ticket.ticketNumber}` : `Ticket ${idx + 1}`;
                  const subject = ticket.subject ? ticket.subject.trim() : "No Subject";
                  const dateStr = ticket.lastMessageAt || ticket.updatedAt || conv.createdAt;
                  const statusStr = String(ticket.status || "open").toLowerCase();

                  return (
                    <div
                      key={conv._id || idx}
                      onClick={() => {
                        if (setSelectedConversation) setSelectedConversation(conv);
                        if (setActiveIcon) setActiveIcon("tickets");
                        setView("message");
                      }}
                      className="p-3 hover:bg-slate-50/80 transition-colors cursor-pointer flex items-center justify-between gap-3 group"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[11px] font-semibold text-slate-500 font-mono">
                            {ticketNum}
                          </span>
                          <span className="text-[10px] text-slate-400 font-normal">
                            • {formatTicketTime(dateStr)}
                          </span>
                        </div>
                        <p className="text-xs font-medium text-slate-800 truncate group-hover:text-indigo-600 transition-colors">
                          {subject}
                        </p>
                      </div>

                      <div className="shrink-0 flex items-center gap-1.5">
                        {renderTicketStatusBadge(statusStr)}
                        <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-slate-600 transition-colors" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* --- Gleap-Style News Cards (Latest / Pinned News) --- */}
      {announcements && announcements.length > 0 ? (
        <div className="space-y-3 mx-4 mt-6">
          {announcements.map((art, idx) => {
            const hasBanner =
              art.bannerImage ||
              art.imageUrl ||
              (art.content?.images && art.content.images[0]);

            return (
              <div
                key={art._id || idx}
                onClick={() => {
                  if (setSelectedArticle) setSelectedArticle(art);
                  if (Boolean(setupDetails?.widgetCustomizations?.news)) {
                    setActiveIcon("news");
                    setView("News");
                  } else {
                    setActiveIcon("help");
                    setView("Help");
                  }
                }}
                className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden hover:shadow-md transition-all cursor-pointer group"
              >
                {/* Cover image or glowing header banner */}
                {hasBanner ? (
                  <div className="relative h-44 w-full overflow-hidden bg-slate-950">
                    <img
                      src={
                        art.bannerImage ||
                        art.imageUrl ||
                        art.content.images[0]
                      }
                      alt={art.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                    <div className="absolute top-3 left-3 bg-black/50 backdrop-blur-md px-2.5 py-1 rounded-full flex items-center gap-1.5 text-[10px] font-bold text-white uppercase tracking-wider border border-white/20">
                      {art.isFeatured ? (
                        <Pin className="w-3 h-3 text-amber-300 fill-amber-300 rotate-45" />
                      ) : (
                        <Sparkles className="w-3 h-3 text-amber-300" />
                      )}
                      <span>{art.isFeatured ? '• News' : 'News'}</span>
                    </div>
                  </div>
                ) : idx === 0 ? (
                  <div 
                    className="h-32 p-4 text-white flex flex-col justify-between relative overflow-hidden"
                    style={{
                      background: `linear-gradient(135deg, ${color1 || '#0C2A4D'} 0%, ${color2 || '#2060EA'} 100%)`,
                    }}
                  >
                    <div className="flex items-center gap-1.5 text-[9px] font-bold text-white/90 uppercase tracking-widest relative z-10">
                      {art.isFeatured ? (
                        <Pin className="w-3.5 h-3.5 text-white/90 fill-white/90 rotate-45" />
                      ) : (
                        <Sparkles className="w-3.5 h-3.5 text-white/90" />
                      )}
                      <span>{art.isFeatured ? 'News' : 'News'}</span>
                    </div>
                    <div className="relative z-10">
                      <div className="text-base font-bold text-white leading-snug line-clamp-2">
                        {art.title}
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="p-4 flex items-center justify-between bg-white">
                  <div className="pr-3 min-w-0">
                    {idx > 0 && !hasBanner && (
                      <div className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100">
                        {art.isFeatured ? (
                          <Pin className="w-3 h-3 rotate-45 fill-blue-600" />
                        ) : (
                          <Sparkles className="w-3 h-3" />
                        )}
                        <span>{art.isFeatured ? ' News' : 'News'}</span>
                      </div>
                    )}
                    <div className="text-sm font-bold text-slate-900 truncate">
                      {art.title}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 line-clamp-2 leading-relaxed">
                      {art.description ||
                        (art.content?.text
                          ? art.content.text.replace(/<[^>]*>/g, '').trim()
                          : 'Read our latest news and updates.')}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            );
          })}
        </div>
      ) 
      : setupDetails?.widgetCustomizations?.aiBot !== false ? (
        <div
          onClick={() => {
            const isLegacyOrBuilder =
              setupDetails?.widgetWorkflow?.workflowId === 'legacy' ||
              (setupDetails?.widgetWorkflow?.enabled && Boolean(setupDetails?.widgetWorkflow?.workflowId) && setupDetails?.widgetWorkflow?.workflowId !== 'dexy_ai');
            setActiveIcon("ai");
            setView(isLegacyOrBuilder ? "AutomatedBot" : "DexyAI");
          }}
          className="mx-4 mt-3 bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden hover:shadow-md transition-all cursor-pointer group"
        >
          <div 
            className="h-32 p-4 text-white flex flex-col justify-between relative overflow-hidden"
            style={{
              background: `linear-gradient(135deg, ${color1 || '#0C2A4D'} 0%, ${color2 || '#2060EA'} 100%)`,
            }}
          >
            {/* Subtle glowing ambient sphere */}
            <div 
              className="absolute right-[-20px] top-[-10px] w-48 h-48 rounded-full opacity-25 pointer-events-none"
              style={{
                background: "radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 70%)",
                filter: "blur(14px)",
              }}
            />
            <div className="flex items-center gap-1.5 text-[9px] font-bold text-white/90 uppercase tracking-widest relative z-10">
              <MessageSquare className="w-3.5 h-3.5 text-white/90" />
              <span>Live Support</span>
            </div>
            <div className="relative z-10">
              <div className="text-base font-bold text-white leading-snug line-clamp-2">
                Send us a message
              </div>
              <div className="text-xs text-white/85 mt-1 line-clamp-1">
                We typically reply within a few minutes
              </div>
            </div>
          </div>
          <div className="p-3.5 flex items-center justify-between bg-white">
            <div className="pr-2 min-w-0">
              <div 
                className="text-sm font-bold truncate transition-colors"
                style={{ color: uiColor || '#0f172a' }}
              >
                Chat with our team
              </div>
              <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                Talk to support, ask questions, or continue your conversation.
              </div>
            </div>
            <ChevronRight 
              className="w-4 h-4 shrink-0 ml-2 group-hover:translate-x-0.5 transition-transform" 
              style={{ color: uiColor || '#64748b' }}
            />
          </div>
        </div>
      ) 
      
      : null}

      {/* --- Search for Help Card (Shown if Help is enabled) --- */}
      {Boolean(setupDetails?.widgetCustomizations?.help) && (
        <div className="mx-4 mt-3 bg-white rounded-2xl border border-slate-200/80 shadow-sm p-3.5">
          <div
            onClick={() => {
              if (setSelectedArticle) setSelectedArticle(null);
              setActiveIcon("help");
              setView("Help");
            }}
            className="bg-slate-100/80 hover:bg-slate-100 rounded-xl px-3.5 py-2.5 flex items-center justify-between cursor-pointer transition-colors mb-2"
          >
            <span className="text-xs font-bold text-slate-900">Search for help</span>
            <Search className="w-3.5 h-3.5 text-slate-700 stroke-[2.5]" />
          </div>
          <div className="divide-y divide-slate-100">
            {loadingArticles && frequentlyReadArticles.length === 0 ? (
              <div className="py-3 px-2 text-center text-xs text-slate-400 animate-pulse">
                Loading articles...
              </div>
            ) : frequentlyReadArticles.length > 0 ? (
              frequentlyReadArticles.map((article) => (
                <div
                  key={article._id || article.slug}
                  onClick={() => {
                    if (setSelectedArticle) setSelectedArticle(article);
                    setActiveIcon("help");
                    setView("Help");
                  }}
                  className="py-2.5 px-1 flex items-center justify-between hover:text-blue-600 cursor-pointer text-xs font-semibold text-slate-700 transition-colors group"
                >
                  <span className="group-hover:text-slate-900 transition-colors line-clamp-1 pr-2">
                    {article.title}
                  </span>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 ml-auto group-hover:translate-x-0.5 transition-transform" />
                </div>
              ))
            ) : (
              <div
                onClick={() => {
                  if (setSelectedArticle) setSelectedArticle(null);
                  setActiveIcon("help");
                  setView("Help");
                }}
                className="py-2.5 px-1 flex items-center justify-between hover:text-blue-600 cursor-pointer text-xs font-semibold text-slate-700 transition-colors group"
              >
                <span className="group-hover:text-slate-900 transition-colors">
                  Browse our Knowledge Base & FAQs
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 ml-2 group-hover:translate-x-0.5 transition-transform" />
              </div>
            )}
          </div>
        </div>
      )}


      {/* --- Dynamic Partner Custom Cards --- */}
      {setupDetails?.widgetCustomizations?.cards?.map(
        (card) =>
          card.enabled && (
            <Card
              key={card.id || card._id}
              {...card}
              primaryColor={primaryColor}
            />
          )
      )}
   
   
     {/* --- Powered by Badge --- */}
      <div className="text-center text-xs text-slate-400 font-medium py-3 flex items-center justify-center gap-1 mt-10">
        <span className="text-amber-400">⚡</span> Powered by{" "}
        <span className="font-bold text-slate-700">Dexkor</span>
      </div>

      {/* --- Extra bottom space to allow scrolling above floating footer --- */}
      <div className="h-24 shrink-0" aria-hidden="true" />
   

    </div>
  );
}

export default LandingWindow;

